/**
 * DemoDrawer - Config-driven drawer component.
 *
 * Supports multiple modes:
 * - "demo-info": For partner demos (explains the product)
 * - "trial-signup": For lead capture (coming soon)
 *
 * All content is driven by the drawer config from client configuration.
 */
export default function DemoDrawer({
  isOpen,
  onClose,
  onOpen,
  config,
  scenarioOptions = [],
  onSelectScenario = null,
}) {
  // If no config or drawer disabled, render nothing
  if (!config?.enabled) {
    return null;
  }

  const {
    mode = null,
    tab_label = 'Info',
    header = {},
    intro = '',
    value_props = [],
    stats = [],
    video = null,
    cta_primary = null,
    cta_secondary = null,
    footer = '',
  } = config;
  const valuePropsHeading = mode === 'demo-info' ? 'What this shows:' : 'Why this matters for you:';
  const statsHeading = mode === 'demo-info' ? 'How to try it live:' : "What we've built:";

  // Convert Loom share URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    // Already an embed URL
    if (url.includes('/embed/')) return url;
    // Share URL - extract ID and convert
    const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (match) return `https://www.loom.com/embed/${match[1]}`;
    // Just a video ID
    if (/^[a-zA-Z0-9]+$/.test(url)) return `https://www.loom.com/embed/${url}`;
    return url;
  };

  return (
    <aside className="fixed inset-0 z-30 pointer-events-none">
      {/* Main drawer panel */}
      <div
        className={`dock-panel pointer-events-auto fixed inset-x-0 bottom-0 h-[90vh] w-full bg-white shadow-2xl border-t border-slate-200 flex flex-col sm:inset-y-0 sm:right-0 sm:bottom-auto sm:left-auto sm:h-full sm:w-[480px] sm:border-l sm:border-t-0 ${
          isOpen ? '' : 'dock-panel-closed'
        }`}
      >
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-5 flex items-center justify-between shrink-0">
          <div className="flex-1">
            {header.eyebrow && (
              <p className="text-[11px] font-semibold tracking-[0.16em] text-white/80 uppercase mb-1">
                {header.eyebrow}
              </p>
            )}
            <h2 className="text-xl font-bold text-white">{header.title || 'About This Demo'}</h2>
          </div>
          <button
            onClick={onClose}
            className="ml-3 shrink-0 rounded-full bg-white/20 hover:bg-white/30 p-2 text-white transition-colors"
            aria-label="Close panel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Intro text */}
          {intro && <p className="text-base text-slate-700 leading-relaxed">{intro}</p>}

          {mode === 'demo-info' && scenarioOptions.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
              <p className="text-sm font-semibold text-slate-900 mb-2">Try live member examples:</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                This assistant is already grounded in live site content. These example members are
                synthetic contexts that show how a real personalized answer changes.
              </p>
              <div className="mt-4 space-y-3">
                {scenarioOptions.slice(0, 2).map((scenario) => (
                  <button
                    key={scenario.code}
                    type="button"
                    onClick={() => onSelectScenario?.(scenario.code)}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{scenario.label}</p>
                        {scenario.persona_blurb && (
                          <p className="mt-1 text-xs leading-relaxed text-slate-600">
                            {scenario.persona_blurb}
                          </p>
                        )}
                      </div>
                      <span className="rounded-full bg-white px-2 py-1 text-[11px] font-medium text-slate-400">
                        {scenario.code}
                      </span>
                    </div>
                    {Array.isArray(scenario.highlights) && scenario.highlights.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {scenario.highlights.slice(0, 2).map((highlight) => (
                          <span
                            key={highlight}
                            className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Value propositions */}
          {value_props.length > 0 && (
            <div className="rounded-xl bg-indigo-50 border border-indigo-100 px-5 py-4">
              <p className="text-sm font-semibold text-slate-900 mb-3">{valuePropsHeading}</p>
              <ul className="space-y-2.5">
                {value_props.map((prop, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="text-indigo-600 mt-0.5 shrink-0">✓</span>
                    <span>{prop}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Stats */}
          {stats.length > 0 && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-5 py-4">
              <p className="text-sm font-semibold text-slate-900 mb-3">{statsHeading}</p>
              <div className="space-y-2">
                {stats.map((stat, index) => (
                  <div key={index} className="flex items-center gap-3 text-sm text-slate-700">
                    <span className="text-lg">{stat.icon}</span>
                    <span>{stat.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Embedded Video */}
          {video && getEmbedUrl(video) && (
            <div className="rounded-xl overflow-hidden border border-slate-200">
              <iframe
                src={getEmbedUrl(video)}
                frameBorder="0"
                allowFullScreen
                className="w-full aspect-video"
                title="Demo video"
              />
            </div>
          )}

          {/* Primary CTA */}
          {cta_primary && (
            <a
              href={cta_primary.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center rounded-full bg-indigo-600 text-white text-sm font-semibold px-6 py-3 hover:bg-indigo-700 transition-colors"
            >
              {cta_primary.label}
            </a>
          )}

          {/* Secondary CTA */}
          {cta_secondary && (
            <a
              href={cta_secondary.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center rounded-full bg-white text-slate-700 text-sm font-semibold px-6 py-3 border border-slate-300 hover:bg-slate-50 transition-colors"
            >
              {cta_secondary.label}
            </a>
          )}

          {/* Footer */}
          {footer && <p className="text-xs text-slate-400 text-center pt-4">{footer}</p>}
        </div>
      </div>

      {/* Tab button (visible when drawer is closed) */}
      {!isOpen && (
        <button
          onClick={onOpen}
          className="dock-tab hidden sm:flex pointer-events-auto fixed right-0 top-1/2 -translate-y-1/2 transform w-12 h-48 items-center justify-center bg-indigo-600 text-white z-30 hover:bg-indigo-700"
        >
          <span className="dock-tab-label text-[11px] font-semibold tracking-[0.18em] uppercase">
            {tab_label}
          </span>
        </button>
      )}
    </aside>
  );
}
